import React, { useState, useEffect } from 'react';
import {
  UsersIcon,
  CurrencyDollarIcon,
  CalendarIcon,
  XCircleIcon,
  MagnifyingGlassIcon,
  ChartBarIcon,
  ChartPieIcon,
  FunnelIcon,
  ArrowPathIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';

interface Subscriber {
  id: number;
  name: string;
  package: string;
  startDate: string;
  paidToDate: string;
  nextDueDate: string;
  totalPaid: number;
  status: 'active' | 'pending' | 'cancelled';
}

type DateRange = 'today' | 'this-week' | 'this-month' | 'last-month' | 'last-3-months' | 'custom';

interface OverviewStats {
  totalSubscribers: number;
  monthlyRevenue: number;
  upcomingRenewals: number;
  cancelled: number;
  trend: {
    subscribers: string;
    revenue: string;
    cancelled: string;
  };
}

interface OverviewStatsData {
  [key: string]: OverviewStats;
}

export const ManageSubscribers: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [chartType, setChartType] = useState('bar');
  const [startPeriod, setStartPeriod] = useState('');
  const [endPeriod, setEndPeriod] = useState('');
  const [chartTitle, setChartTitle] = useState('Subscriber Growth');
  const [filterDateRange, setFilterDateRange] = useState<DateRange>('this-month');
  const [filterPackage, setFilterPackage] = useState('all');
  const [showOverviewExportDropdown, setShowOverviewExportDropdown] = useState(false);
  const [showDetailedExportDropdown, setShowDetailedExportDropdown] = useState(false);

  // Mock data for subscribers
  const subscribers: Subscriber[] = [
    {
      id: 1,
      name: 'John Smith',
      package: 'Premium',
      startDate: '2024-01-01',
      paidToDate: '2024-03-01',
      nextDueDate: '2024-04-01',
      totalPaid: 299.97,
      status: 'active'
    },
    // Add more mock data as needed
  ];

  // Update the overviewStats declaration with the OverviewStatsData type
  const overviewStats: OverviewStatsData = {
    'this-month': {
      totalSubscribers: 1234,
      monthlyRevenue: 12345,
      upcomingRenewals: 156,
      cancelled: 23,
      trend: {
        subscribers: '+12%',
        revenue: '+8%',
        cancelled: '-2%'
      }
    },
    'last-month': {
      totalSubscribers: 1100,
      monthlyRevenue: 11400,
      upcomingRenewals: 145,
      cancelled: 25,
      trend: {
        subscribers: '+10%',
        revenue: '+7%',
        cancelled: '-1%'
      }
    },
    // Add more date ranges as needed
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const filteredSubscribers = subscribers.filter(subscriber =>
    subscriber.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Add this function to handle filter changes
  const handleExportOverviewData = () => {
    // Implementation for exporting data
    console.log('Exporting overview data...');
  };

  const handleOverviewExport = (format: 'pdf' | 'csv') => {
    console.log(`Exporting overview data as ${format}...`);
    setShowOverviewExportDropdown(false);
  };

  const handleDetailedExport = (format: 'pdf' | 'csv') => {
    console.log(`Exporting detailed data as ${format}...`);
    setShowDetailedExportDropdown(false);
  };

  // Add this useEffect after the state declarations
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showOverviewExportDropdown || showDetailedExportDropdown) {
        const target = event.target as HTMLElement;
        if (!target.closest('.relative')) {
          setShowOverviewExportDropdown(false);
          setShowDetailedExportDropdown(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showOverviewExportDropdown, showDetailedExportDropdown]);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Manage Subscribers</h1>
          <p className="mt-2 text-sm text-gray-600">Overview and management of platform subscribers</p>
        </div>

        {/* Section 1: Subscribers Overview */}
        <div className="mb-8">
          <div className="bg-white rounded-lg shadow-md p-6">
            {/* Filters Row */}
            <div className="flex justify-between items-center mb-6">
              <div className="flex space-x-4">
                <div>
                  <select
                    value={filterDateRange}
                    onChange={(e) => setFilterDateRange(e.target.value as DateRange)}
                    className="border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-[#FF5722]"
                  >
                    <option value="this-month">This Month</option>
                    <option value="last-month">Last Month</option>
                    <option value="last-3-months">Last 3 Months</option>
                    <option value="custom">Custom Range</option>
                  </select>
                </div>

                <div>
                  <select
                    value={filterPackage}
                    onChange={(e) => setFilterPackage(e.target.value)}
                    className="border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-[#FF5722]"
                  >
                    <option value="all">All Packages</option>
                    <option value="basic">Basic</option>
                    <option value="professional">Professional</option>
                    <option value="enterprise">Enterprise</option>
                  </select>
                </div>

                {/* Show date inputs if custom range is selected */}
                {filterDateRange === 'custom' && (
                  <div className="flex space-x-2">
                    <input
                      type="date"
                      className="border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-[#FF5722]"
                    />
                    <input
                      type="date"
                      className="border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-[#FF5722]"
                    />
                  </div>
                )}
              </div>

              <div className="relative">
                <button
                  onClick={() => setShowOverviewExportDropdown(!showOverviewExportDropdown)}
                  className="flex items-center bg-[#FF5722] text-white px-4 py-2 rounded-md hover:bg-[#F4511E]"
                >
                  <ArrowDownTrayIcon className="w-5 h-5 mr-2" />
                  Export Data
                </button>
                {showOverviewExportDropdown && (
                  <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                    <div className="py-1" role="menu" aria-orientation="vertical">
                      <button
                        onClick={() => handleOverviewExport('pdf')}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                        role="menuitem"
                      >
                        Export as PDF
                      </button>
                      <button
                        onClick={() => handleOverviewExport('csv')}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                        role="menuitem"
                      >
                        Export as CSV
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {/* Total Subscribers */}
              <div className="bg-gray-50 rounded-lg p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Total Subscribers</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {overviewStats[filterDateRange]?.totalSubscribers || 0}
                    </p>
                  </div>
                  <UsersIcon className="w-12 h-12 text-[#FF5722]" />
                </div>
                <p className="mt-2 text-sm text-green-600">
                  {overviewStats[filterDateRange]?.trend.subscribers || '+0%'} from last period
                </p>
              </div>

              {/* Monthly Revenue */}
              <div className="bg-gray-50 rounded-lg p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Monthly Revenue</p>
                    <p className="text-2xl font-bold text-gray-900">
                      ${overviewStats[filterDateRange]?.monthlyRevenue || 0}
                    </p>
                  </div>
                  <CurrencyDollarIcon className="w-12 h-12 text-[#FF5722]" />
                </div>
                <p className="mt-2 text-sm text-green-600">
                  {overviewStats[filterDateRange]?.trend.revenue || '+0%'} from last period
                </p>
              </div>

              {/* Upcoming Renewals */}
              <div className="bg-gray-50 rounded-lg p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Upcoming Renewals</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {overviewStats[filterDateRange]?.upcomingRenewals || 0}
                    </p>
                  </div>
                  <CalendarIcon className="w-12 h-12 text-[#FF5722]" />
                </div>
                <p className="mt-2 text-sm text-blue-600">Next 30 days</p>
              </div>

              {/* Cancelled */}
              <div className="bg-gray-50 rounded-lg p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Cancelled</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {overviewStats[filterDateRange]?.cancelled || 0}
                    </p>
                  </div>
                  <XCircleIcon className="w-12 h-12 text-[#FF5722]" />
                </div>
                <p className="mt-2 text-sm text-red-600">
                  {overviewStats[filterDateRange]?.trend.cancelled || '0%'} from last period
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Section 2: Subscribers Detailed */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-bold text-gray-900">Subscribers Detailed</h2>
            <div className="flex items-center space-x-4">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search subscribers..."
                  value={searchTerm}
                  onChange={handleSearch}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722]"
                />
                <MagnifyingGlassIcon className="w-5 h-5 text-gray-400 absolute left-3 top-2.5" />
              </div>
              <div className="relative">
                <button
                  onClick={() => setShowDetailedExportDropdown(!showDetailedExportDropdown)}
                  className="flex items-center bg-[#FF5722] text-white px-4 py-2 rounded-md hover:bg-[#F4511E]"
                >
                  <ArrowDownTrayIcon className="w-5 h-5 mr-2" />
                  Export Data
                </button>
                {showDetailedExportDropdown && (
                  <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                    <div className="py-1" role="menu" aria-orientation="vertical">
                      <button
                        onClick={() => handleDetailedExport('pdf')}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                        role="menuitem"
                      >
                        Export as PDF
                      </button>
                      <button
                        onClick={() => handleDetailedExport('csv')}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                        role="menuitem"
                      >
                        Export as CSV
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Subscriber</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Package</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Start Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Paid to Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Next Due Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Paid</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredSubscribers.map((subscriber) => (
                  <tr key={subscriber.id}>
                    <td className="px-4 py-4 text-sm text-gray-900">{subscriber.name}</td>
                    <td className="px-4 py-4 text-sm text-gray-900">{subscriber.package}</td>
                    <td className="px-4 py-4 text-sm text-gray-900">{subscriber.startDate}</td>
                    <td className="px-4 py-4 text-sm text-gray-900">{subscriber.paidToDate}</td>
                    <td className="px-4 py-4 text-sm text-gray-900">{subscriber.nextDueDate}</td>
                    <td className="px-4 py-4 text-sm text-gray-900">${subscriber.totalPaid}</td>
                    <td className="px-4 py-4 text-sm">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        subscriber.status === 'active' 
                          ? 'bg-green-100 text-green-800'
                          : subscriber.status === 'pending'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {subscriber.status}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-sm text-[#FF5722] hover:text-[#F4511E]">
                      <button>View Details</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Section 3: Subscriber Charts */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-bold text-gray-900">Subscriber Charts</h2>
            <div className="flex items-center space-x-4">
              <button className="flex items-center text-[#FF5722] hover:text-[#F4511E]">
                <ArrowPathIcon className="w-5 h-5 mr-1" />
                Refresh Data
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Chart Title
              </label>
              <input
                type="text"
                value={chartTitle}
                onChange={(e) => setChartTitle(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722]"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Chart Type
              </label>
              <select
                value={chartType}
                onChange={(e) => setChartType(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722]"
              >
                <option value="bar">Bar Chart</option>
                <option value="line">Line Chart</option>
                <option value="pie">Pie Chart</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Period
              </label>
              <input
                type="date"
                value={startPeriod}
                onChange={(e) => setStartPeriod(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722]"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                End Period
              </label>
              <input
                type="date"
                value={endPeriod}
                onChange={(e) => setEndPeriod(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722]"
              />
            </div>
          </div>

          {/* Chart Placeholder */}
          <div className="h-96 bg-gray-50 rounded-lg flex items-center justify-center">
            <span className="text-gray-500">Chart Placeholder</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageSubscribers; 